import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Globe, Download, MessageCircle, FileText, LogIn, UserPlus,
  Facebook, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Vantage-style mobile mega menu — stack navigation with slide-in transitions.
 * Level 0: Top-level categories (text only) + utility links (with icons) + footer.
 * Level 1+: Category sub-items with icons. Slides in from right on click; back arrow returns.
 */
const MobileMenu = ({ open, onClose, items }) => {
  const { isRTL, language, changeLanguage, availableLanguages, t } = useLanguage();
  const [stack, setStack] = useState([]); // each entry: { label, items }

  const push = (label, subItems) => setStack([...stack, { label, items: subItems }]);
  const pop = () => setStack(stack.slice(0, -1));
  const reset = () => setStack([]);
  const handleClose = () => { reset(); onClose(); };

  const current = stack.length > 0 ? stack[stack.length - 1] : null;
  const slideFrom = isRTL ? -1 : 1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] lg:hidden bg-white"
          dir={isRTL ? 'rtl' : 'ltr'}
          data-testid="mobile-menu"
        >
          {/* Slide-down container */}
          <motion.div
            initial={{ y: '-100%' }}
            animate={{ y: 0, transition: { duration: 0.32, ease: [0.32, 0.72, 0, 1] } }}
            exit={{ y: '-100%', transition: { duration: 0.24 } }}
            className="absolute inset-0 flex flex-col bg-white overflow-hidden"
          >
            {/* HEADER bar with logo + back/close */}
            <div className="flex items-center justify-between px-5 h-16 border-b border-[rgba(20,15,10,0.08)]">
              {stack.length > 0 ? (
                <button onClick={pop} className="p-2 -ms-2 text-ink-900" aria-label="Back" data-testid="mobile-back">
                  {isRTL ? <ChevronRight className="w-6 h-6" /> : <ChevronLeft className="w-6 h-6" />}
                </button>
              ) : (
                <span className="text-[18px] font-black tracking-tight">
                  <span className="text-ink-900">VARO</span><span className="varo-text-flame">TRADES</span>
                </span>
              )}
              <button onClick={handleClose} className="p-2 -me-2 text-ink-900" aria-label="Close" data-testid="mobile-close">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* CONTENT — animates between levels */}
            <div className="flex-1 relative overflow-hidden">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={stack.length}
                  initial={{ x: `${slideFrom * 100}%`, opacity: 0.6 }}
                  animate={{ x: 0, opacity: 1, transition: { duration: 0.28, ease: [0.32, 0.72, 0, 1] } }}
                  exit={{ x: `${-slideFrom * 30}%`, opacity: 0, transition: { duration: 0.2 } }}
                  className="absolute inset-0 overflow-y-auto"
                >
                  {stack.length === 0 ? (
                    <RootLevel items={items} onSelect={handleClose} onPush={push} isRTL={isRTL} t={t} availableLanguages={availableLanguages} language={language} changeLanguage={changeLanguage} />
                  ) : (
                    <SubLevel level={current} onSelect={handleClose} onPush={push} isRTL={isRTL} />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ─── ROOT LEVEL ─── */
const RootLevel = ({ items, onSelect, onPush, isRTL, t, availableLanguages, language, changeLanguage }) => (
  <div className="pb-8" data-testid="mobile-root-level">
    {/* Top-level categories — TEXT ONLY (no icons) */}
    <div className="py-2">
      <Link to="/" onClick={onSelect} className="flex items-center justify-between px-5 py-4 hover:bg-[rgba(20,15,10,0.04)]">
        <span className="text-[16px] text-ink-900 font-medium">{isRTL ? 'الرئيسية' : 'Home'}</span>
      </Link>
      {items.map((item) => (
        <button
          key={item.id}
          onClick={() => onPush(item.label, item.items)}
          className={`w-full flex items-center justify-between px-5 py-4 hover:bg-[rgba(20,15,10,0.04)] ${isRTL ? 'flex-row-reverse text-right' : ''}`}
          data-testid={`mobile-cat-${item.id}`}
        >
          <span className="text-[16px] text-ink-900 font-medium">{item.label}</span>
          {isRTL ? <ChevronLeft className="w-5 h-5 text-ink-400" /> : <ChevronRight className="w-5 h-5 text-ink-400" />}
        </button>
      ))}
    </div>

    {/* Utility links with icons */}
    <div className="border-t border-[rgba(20,15,10,0.06)] py-2">
      <UtilityLink icon={Globe} label={availableLanguages.find(l => l.code === language)?.name || 'English'} onClick={() => {}} isRTL={isRTL} />
      <UtilityLink icon={Download} label={isRTL ? 'تحميل التطبيق' : 'Download App'} to="/app" onClick={onSelect} isRTL={isRTL} />
      <UtilityLink icon={MessageCircle} label={isRTL ? 'الدردشة المباشرة' : 'Live Chat'} onClick={() => {}} isRTL={isRTL} />
    </div>

    {/* Quick language pills */}
    <div className="px-5 py-3 border-t border-[rgba(20,15,10,0.06)] flex flex-wrap gap-2">
      {availableLanguages.map(l => (
        <button key={l.code} onClick={() => changeLanguage(l.code)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${language === l.code ? 'bg-ink-900 text-white' : 'bg-[rgba(20,15,10,0.05)] text-ink-700'}`}>
          {l.flag} {l.name}
        </button>
      ))}
    </div>

    {/* CTA buttons */}
    <div className="px-5 py-4 grid grid-cols-2 gap-3 border-t border-[rgba(20,15,10,0.06)]">
      <Link to="/login" onClick={onSelect} className="h-12 inline-flex items-center justify-center gap-2 rounded-full border border-ink-900 text-ink-900 font-bold text-sm" data-testid="mobile-login">
        <LogIn className="w-4 h-4" /> {t('login')}
      </Link>
      <Link to="/register" onClick={onSelect} className="h-12 inline-flex items-center justify-center gap-2 rounded-full bg-flame-500 hover:bg-flame-600 text-white font-bold text-sm" data-testid="mobile-register">
        <UserPlus className="w-4 h-4" /> {t('register')}
      </Link>
    </div>

    {/* Social icons */}
    <div className="px-5 py-4 flex items-center justify-center gap-5 border-t border-[rgba(20,15,10,0.06)]">
      {[
        { Icon: Facebook, href: 'https://facebook.com' },
        { Icon: Instagram, href: 'https://instagram.com' },
        { Icon: Twitter, href: 'https://x.com' },
        { Icon: Linkedin, href: 'https://linkedin.com' },
        { Icon: Youtube, href: 'https://youtube.com' },
      ].map(({ Icon, href }, i) => (
        <a key={i} href={href} target="_blank" rel="noreferrer noopener" className="text-ink-600 hover:text-flame-500 transition-colors">
          <Icon className="w-5 h-5" />
        </a>
      ))}
    </div>

    {/* Legal links */}
    <div className="px-5 py-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-ink-500 border-t border-[rgba(20,15,10,0.06)]">
      <Link to="/company" onClick={onSelect} className="hover:text-flame-600">{isRTL ? 'عن الشركة' : 'About Us'}</Link>
      <Link to="/legal/terms" onClick={onSelect} className="hover:text-flame-600">{isRTL ? 'الشروط والأحكام' : 'Terms & Conditions'}</Link>
      <Link to="/legal/privacy" onClick={onSelect} className="hover:text-flame-600">{isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}</Link>
      <Link to="/legal" onClick={onSelect} className="inline-flex items-center gap-1 hover:text-flame-600"><FileText className="w-3 h-3" /> {isRTL ? 'الوثائق القانونية' : 'Legal Documents'}</Link>
    </div>

    {/* Logo footer */}
    <div className="px-5 py-6 text-center text-ink-300 text-xs">
      <span className="text-[18px] font-black tracking-tight inline-block opacity-50">
        <span className="text-ink-900">VARO</span><span className="varo-text-flame">TRADES</span>
      </span>
      <p className="mt-2">© 2026 VAROTRADES. All rights reserved.</p>
    </div>
  </div>
);

const UtilityLink = ({ icon: Icon, label, to, onClick, isRTL }) => {
  const Inner = (
    <div className={`flex items-center gap-3 px-5 py-3.5 hover:bg-[rgba(20,15,10,0.04)] ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
      <Icon className="w-5 h-5 text-ink-700 shrink-0" strokeWidth={1.7} />
      <span className="text-[15px] text-ink-800 font-medium flex-1">{label}</span>
    </div>
  );
  return to ? <Link to={to} onClick={onClick}>{Inner}</Link> : <button onClick={onClick} className="block w-full text-left">{Inner}</button>;
};

/* ─── SUB-LEVEL (after clicking a category) ─── */
const SubLevel = ({ level, onSelect, onPush, isRTL }) => (
  <div className="pb-8" data-testid="mobile-sub-level">
    {/* Category title header */}
    <div className="px-5 pt-5 pb-2">
      <p className="text-[11px] uppercase tracking-[0.2em] text-ink-400 font-bold">{level.label}</p>
    </div>

    {/* Items WITH icons */}
    <div>
      {level.items.map((it, i) => {
        const hasChildren = Array.isArray(it.children) && it.children.length > 0;
        const Icon = it.icon;
        const Inner = (
          <div className={`flex items-start gap-3 px-5 py-4 hover:bg-[rgba(20,15,10,0.04)] ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
            {Icon && (
              <div className="shrink-0 w-9 h-9 rounded-lg bg-white border border-[rgba(20,15,10,0.08)] flex items-center justify-center mt-0.5">
                <Icon className="w-[18px] h-[18px] text-ink-900" strokeWidth={1.7} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="text-[15px] text-ink-900 font-semibold leading-tight">{it.label}</span>
                {it.badge && <span className="px-1.5 py-0.5 rounded text-white text-[9px] font-bold uppercase tracking-wider bg-[#E63946]">{it.badge}</span>}
              </div>
              {it.desc && <p className="text-[12px] text-ink-500 mt-1 leading-snug">{it.desc}</p>}
            </div>
            {hasChildren && (isRTL ? <ChevronLeft className="w-5 h-5 text-ink-400 shrink-0 mt-2" /> : <ChevronRight className="w-5 h-5 text-ink-400 shrink-0 mt-2" />)}
          </div>
        );
        return hasChildren ? (
          <button key={i} onClick={() => onPush(it.label, it.children)} className="block w-full" data-testid={`mobile-subcat-${i}`}>{Inner}</button>
        ) : (
          <Link key={i} to={it.link || '#'} onClick={onSelect} data-testid={`mobile-link-${i}`}>{Inner}</Link>
        );
      })}
    </div>
  </div>
);

export default MobileMenu;
